import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as toastr from 'toastr';

import policyActions from '../policy/policyActions';

import PolicyBeneficiaryForm from './PolicyBeneficiaryForm';
import { UtilsBeneficiaries, BeneficiaryTypes } from '../common/utils';
import { defaultTrustee } from './policyAPI';

interface Props {
	baseUrl?: string;
	customerPolicyBeneficiary: CustomerPolicyBeneficiaryDto;
	policies: CustomerPolicy[];
	currentBeneficiaries: CustomerPolicyBeneficiaryDto[];
	pendingBeneficiaries: CustomerPolicyBeneficiaryDto[];
	actions: any;
	history: any;
	match: any;
}
interface State {
	loading: boolean;
}
class PolicyBeneficiaryContainer extends React.Component<Props, State> {
	state = {
		loading: false
	};

	componentDidMount() {
		this.setState({loading: true});
		const promises = [];
		const userId = this.props.match.params.userId || this.props.match.params.customerId;
		const beneficiaryId = this.props.match.params.beneficiaryId || this.props.match.params.pageId;
		promises.push(this.props.actions.fetchCustomerPolicies(userId));
		promises.push(this.props.actions.fetchCustomerPolicyBeneficiaries(userId)); 
		if (beneficiaryId === 'new') {
			promises.push(this.props.actions.resetCustomerPolicyBeneficiary());
		}
		else {
			promises.push(this.props.actions.fetchCustomerPolicyBeneficiary(userId, beneficiaryId));
		}
		Promise.all(promises)
		.then(() => {
			this.setState({loading: false});
		});
	}

	onPolicyBeneficiaryChange = (e: any) => {
		let value = e.target.value;
		if (e.target.type === 'radio') {
			if (e.target.name === 'typeId') {
				if (value === 'true') {
					value = BeneficiaryTypes.Primary;
				} else if (value === 'false') {
					value = BeneficiaryTypes.Contingent;
				}
			} else {
				if (value === 'true') {
					value = true;
				} else if (value === 'false') {
					value = false;
				}
			}
		}
		const customerPolicyBeneficiary = {
			...this.props.customerPolicyBeneficiary,
			[e.target.name]: value
		};

		this.props.actions.setCustomerPolicyBeneficiary(customerPolicyBeneficiary);
	}

	onRelationshipChange = (e: any) => {
		let value = e.target.value;
		const customerPolicyBeneficiary = {
			...this.props.customerPolicyBeneficiary,
			[e.target.name]: value
		};

		if (e.target.value !== 'Other') {
			customerPolicyBeneficiary.relationshipOther = '';
		}

		this.props.actions.setCustomerPolicyBeneficiary(customerPolicyBeneficiary);
	}

	save = () => {
		const beneficiary = { ...this.props.customerPolicyBeneficiary };
		// reset the disbursement percent if assigned policy is changed
		if (beneficiary.typeId === BeneficiaryTypes.Primary) {
			const isPrimarySubsequent = this.props.pendingBeneficiaries
			.some(b => b.customerPolicyId === beneficiary.customerPolicyId &&
				b.id !== beneficiary.id && b.typeId === BeneficiaryTypes.Primary);
			if (isPrimarySubsequent) {
				beneficiary.disbursementPercent = beneficiary.disbursementPercent || 0;
			} else {
				beneficiary.disbursementPercent = beneficiary.disbursementPercent || 1;
			}
		} else {
			const isContingentSubsequent = this.props.pendingBeneficiaries
			.some(b => b.customerPolicyId === beneficiary.customerPolicyId &&
				b.id !== beneficiary.id && b.typeId === BeneficiaryTypes.Contingent);
			if (isContingentSubsequent) {
				beneficiary.disbursementPercent = beneficiary.disbursementPercent || 0;
			} else {
				beneficiary.disbursementPercent = beneficiary.disbursementPercent || 1;
			}
		}

		return this.props.actions.saveCustomerPolicyBeneficiary(beneficiary);
	}

	saveAndNext = () => {
		const userId = this.props.match.params.userId || this.props.match.params.customerId;
		this.save()
			.then(() => {
				this.props.actions.fetchCustomerPolicyBeneficiaries(userId)
				.then(() => {
					toastr.success('Beneficiary updated!');
					const policy = this.props.policies.find(p => p.id === this.props.customerPolicyBeneficiary.customerPolicyId);
					this.props.actions.setCustomerPolicy(policy);
					const filteredBeneficiaries = this.props.pendingBeneficiaries
						.filter(b => b.customerPolicyId === policy!.id);
					this.props.actions.setCustomerPolicyBeneficiaries(filteredBeneficiaries);
					
					if (this.props.pendingBeneficiaries.some(b => b.isUnderage === true)) {
						const trustee = this.props.pendingBeneficiaries.find(b => b.typeId === BeneficiaryTypes.Trustee);
						if (trustee) {
							this.props.actions.setTrustee(trustee);
						} else {
							let newTrustee = defaultTrustee;
							this.props.actions.setTrustee(newTrustee);
						}
					}

					if (this.props.baseUrl) {
						this.props.history.push(this.props.baseUrl + '/beneficiaries-disbursement');
					} 
					else {
						this.props.history.push(`/user/${userId}/beneficiaries-disbursement`);
					}
				});
			});
			
	}

	saveAndAdd = () => {
		const userId = this.props.match.params.userId || this.props.match.params.customerId;
		this.save()
			.then(this.props.actions.resetCustomerPolicyBeneficiary)
			.then(() => {
				if (this.props.baseUrl) {
					this.props.history.push(this.props.baseUrl + '/beneficiaries/new');
				} else {
					this.props.history.push(`/user/${userId}/beneficiaries/new`);
				}
			});
	}

	cancel = () => {
		const userId = this.props.match.params.userId || this.props.match.params.customerId;
		if (this.props.baseUrl) {
			this.props.history.push(this.props.baseUrl + '/beneficiaries');
		} else {
			this.props.history.push(`/user/${userId}/beneficiaries`);
		}
		
	}

	render() {
		return !this.state.loading && (
			<PolicyBeneficiaryForm
				customerPolicyBeneficiary={this.props.customerPolicyBeneficiary!}
				policies={this.props.policies!}
				onPolicyBeneficiaryChange={this.onPolicyBeneficiaryChange}
				onRelationshipChange={this.onRelationshipChange}
				save={this.saveAndNext}
				saveAndAdd={this.saveAndAdd}
				match={this.props.match}
				cancel={this.cancel}
			/>
		);
	}
}
function mapStateToProps(state: any, props: any) {
	let beneficiary = state.policy.customerPolicyBeneficiary;
	const userId = props.match.params.userId || props.match.params.customerId;
	const beneficiaryId = props.match.params.beneficiaryId || props.match.params.pageId;
	if (beneficiaryId === 'new') {
		beneficiary = { ...beneficiary, customerId: userId };
	}

	const currentBeneficiaries = state.policy.beneficiaries.current
	.sort(UtilsBeneficiaries.sortPrimaryThenByName);
	const pendingBeneficiaries = state.policy.beneficiaries.pending
	.sort(UtilsBeneficiaries.sortPrimaryThenByName);

	return {
		customerPolicyBeneficiary: beneficiary,
		policies: state.policy.policies,
		currentBeneficiaries,
		pendingBeneficiaries,
	};
}
function mapDispatchToProps(dispatch: any) {
	const actions = { ...policyActions };
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}
export default connect(mapStateToProps, mapDispatchToProps)(PolicyBeneficiaryContainer);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/policy/PolicyBeneficiaryContainer.tsx